/**
 * CampaignReferencePanel
 * --------------------------------------------------------------------------
 * Displays the targeting parameters of one or more campaigns in a sticky
 * right-hand panel: country, industry, employee size, revenue band, departments,
 * job functions, opt-in policy, assets, special instructions, AND downloadable
 * script attachments (PDF/DOCX/XLSX/PPTX/TXT) uploaded by admin.
 *
 * Used on:
 *   - LeadForm.jsx          (single campaign chosen via the campaign_name field)
 *   - CampaignExclusion.jsx (one OR many campaigns chosen via the Search Scope)
 *
 * Pass either a single `campaign` object OR an array via `campaigns`. The
 * single-campaign path keeps the original API used by LeadForm for backward
 * compatibility.
 */
import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function CampaignReferencePanel({ campaign, campaigns }) {
  const list = Array.isArray(campaigns)
    ? campaigns.filter(Boolean)
    : (campaign ? [campaign] : []);

  if (list.length === 0) {
    return (
      <aside
        className="border border-border bg-secondary/30 p-6 sticky top-4 rounded-sm"
        data-testid="campaign-reference-empty"
      >
        <div className="meta-label mb-2">Campaign Parameters</div>
        <p className="text-sm text-muted-foreground">
          Pick a campaign to see its targeting criteria here — country, industry,
          employee size, revenue band, departments, opt-in policy and special
          instructions from the campaign owner.
        </p>
      </aside>
    );
  }

  // Scroll container so the panel stays inside the viewport regardless of how
  // many campaigns the user has selected. Sticky positioning is on the outer
  // wrapper; the inner `overflow-y-auto` provides the scrollbar.
  return (
    <div className="sticky top-4" data-testid="campaign-reference-list">
      <div
        className="space-y-3 overflow-y-auto pr-1"
        style={{ maxHeight: "calc(100vh - 6rem)" }}
      >
        {list.map((c) => <SingleCampaignCard key={c.id || c.name} campaign={c} />)}
      </div>
    </div>
  );
}

function SingleCampaignCard({ campaign }) {
  const rows = [
    { label: "Description", value: campaign.description },
    { label: "Target Countries", value: (campaign.target_countries || []).join(", ") },
    { label: "Target Industries", value: (campaign.target_industries || []).join(", ") },
    { label: "Employee Size", value: (campaign.target_employee_sizes || []).join(", ") },
    { label: "Revenue Size", value: (campaign.target_revenue_sizes || []).join(", ") },
    { label: "Departments", value: (campaign.target_departments || []).join(", ") },
    { label: "Job Functions", value: campaign.target_job_functions },
    { label: "Opt-in Required", value: campaign.opt_in_required ? "Yes" : "No" },
    { label: "Assets", value: campaign.assets },
    { label: "Special Instructions", value: campaign.special_instructions },
  ];
  return (
    <aside
      className="border border-border bg-secondary/30 rounded-sm"
      data-testid={`campaign-reference-${campaign.name}`}
    >
      <header className="px-5 py-4 border-b border-border">
        <div className="meta-label">Campaign Parameters</div>
        <h3
          className="font-display text-lg font-bold leading-tight mt-1"
          data-testid="campaign-reference-name"
        >
          {campaign.name}
        </h3>
      </header>
      <div className="p-5 space-y-4">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="meta-label">{r.label}</div>
            <div className="text-sm mt-1 break-words">
              {r.value && String(r.value).trim()
                ? r.value
                : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        ))}
        {/* Script — downloadable attachments (PDF/DOCX/XLSX/PPTX/TXT) that
            admin uploaded via Campaign Editor. Rendered below Special
            Instructions so agents see the docs while filling the form. */}
        <CampaignScriptAttachments campaignId={campaign.id} />
      </div>
    </aside>
  );
}

function CampaignScriptAttachments({ campaignId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // attachment.id currently downloading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!campaignId) { setItems([]); setLoading(false); return; }
      try {
        const { data } = await api.get(`/campaigns/${campaignId}/attachments`);
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  const download = async (a) => {
    setBusy(a.id);
    try {
      const resp = await api.get(`/campaigns/${campaignId}/attachments/${a.id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: a.mime_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={`campaign-script-attachments-${campaignId || "none"}`}>
      <div className="meta-label">Script</div>
      {loading ? (
        <div className="text-sm mt-1 text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm mt-1 text-muted-foreground">—</div>
      ) : (
        <ul className="mt-1 space-y-1">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm">
              <FileText size={13} className="text-muted-foreground shrink-0" />
              <button
                type="button"
                onClick={() => download(a)}
                disabled={busy === a.id}
                className="flex-1 min-w-0 text-left hover:underline text-primary truncate"
                data-testid={`script-download-${a.id}`}
              >
                {busy === a.id ? "Downloading…" : (a.label || a.filename)}
              </button>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">{humanKB(a.size)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function humanKB(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
